import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useGameStore from '../../store/gameStore';
import { Bell, GraduationCap, FileCheck, Check, Trash2, Truck, CreditCard, Trophy } from 'lucide-react';

export const GameNotifications = () => {
  const { notifications, markNotificationRead, clearNotifications, gameStarted } = useGameStore();
  const [open, setOpen] = useState(false);
  const bellRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (open && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      // If more space on the right, open to the right; otherwise open aligned to the bell
      if (spaceRight >= 320) {
        setPos({ top: rect.bottom + 8, left: rect.left, right: 'auto' });
      } else {
        setPos({ top: rect.bottom + 8, left: 'auto', right: Math.max(8, spaceRight) });
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          bellRef.current && !bellRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!gameStarted) return null;

  const unread = (notifications || []).filter(n => !n.read);
  const all = (notifications || []).slice().reverse();

  const getIcon = (type) => {
    switch (type) {
      case 'training_complete': return <GraduationCap className="w-4 h-4 text-accent" />;
      case 'contract_complete': return <FileCheck className="w-4 h-4 text-money" />;
      case 'lease_expired': return <CreditCard className="w-4 h-4 text-purple-500" />;
      case 'vehicle_purchased': return <Truck className="w-4 h-4 text-accent" />;
      case 'vehicle_sold': return <Truck className="w-4 h-4 text-danger" />;
      case 'achievement': return <Trophy className="w-4 h-4 text-warn" />;
      default: return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  return (
    <div className="relative" data-testid="notifications-center">
      <button
        ref={bellRef}
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-ink-800/10 transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="w-5 h-5 text-foreground" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-danger/100 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unread.length > 9 ? '9+' : unread.length}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 max-h-96 bg-ink-800 rounded-xl shadow-2xl border border-ink-600 z-[100] overflow-hidden"
          style={{ top: pos.top, left: pos.left !== 'auto' ? pos.left : undefined, right: pos.right !== 'auto' ? pos.right : undefined }}
          data-testid="notifications-dropdown"
        >
          <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-200">Notifications</h3>
            {all.length > 0 && (
              <button
                onClick={() => { clearNotifications(); setOpen(false); }}
                className="text-[10px] text-slate-500 hover:text-danger flex items-center gap-0.5 transition-colors"
                data-testid="clear-all-notifications"
              >
                <Trash2 className="w-3 h-3" /> Tout effacer
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-72">
            {all.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">
                Aucune notification
              </div>
            ) : (
              all.map(n => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-gray-50 flex items-start gap-3 transition-colors ${
                    !n.read ? 'bg-accent/5' : ''
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">{getIcon(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium ${!n.read ? 'text-slate-100' : 'text-slate-400'}`}>{n.title}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">{n.message}</p>
                    <p className="text-[10px] text-slate-600 mt-1">Jour {n.day}</p>
                  </div>
                  {!n.read && (
                    <button
                      onClick={() => markNotificationRead(n.id)}
                      className="text-slate-600 hover:text-money transition-colors flex-shrink-0 mt-0.5"
                      data-testid={`mark-read-${n.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default GameNotifications;
